/**
 * CalendarSubscribeButton — reusable dropdown button that fetches a persistent
 * user-scoped iCal token from `endpoint` and offers 3 subscription options:
 *   - webcal://  (iPhone / Mac — one-tap subscribe on Safari)
 *   - HTTPS      (Google Calendar / Outlook — paste URL when adding calendar)
 *   - Download   (.ics one-time import)
 *
 * Includes an inline 3-step guide in Bahasa Melayu.
 *
 * Usage:
 *   <CalendarSubscribeButton
 *      endpoint="/api/tasks/ical-token"
 *      feedPath="/api/tasks/ical"
 *      label="Sync to Calendar" />
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Calendar, Smartphone, MonitorSmartphone, Download, Copy, CheckCircle,
  ChevronDown, HelpCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CalendarSubscribeButton({
  endpoint,
  feedPath,
  label = 'Sync to Calendar',
  testId = 'btn-calendar-subscribe',
}) {
  const [icalUrl, setIcalUrl] = useState('');
  const [copied, setCopied] = useState('');
  const [showGuide, setShowGuide] = useState(false);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API}${endpoint}`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.ok) {
          const d = await res.json();
          setIcalUrl(`${API}${feedPath}/${d.token}`);
        } else {
          toast.error('Calendar sync unavailable — please retry later');
        }
      } catch {
        toast.error('Calendar sync unavailable — network error');
      }
    })();
  }, [endpoint, feedPath]);

  const doCopy = async (kind) => {
    if (!icalUrl) return;
    const url = kind === 'webcal' ? icalUrl.replace(/^https?:/, 'webcal:') : icalUrl;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(kind);
      toast.success(`${kind === 'webcal' ? 'webcal://' : 'HTTPS'} URL copied — paste dalam calendar app anda`);
      setTimeout(() => setCopied(''), 2500);
    } catch {
      toast.error('Clipboard blocked. Copy manually: ' + url);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="border-[hsl(var(--border))] text-[hsl(var(--text-secondary))] hover:text-white"
          data-testid={testId}
        >
          <Calendar size={14} className="mr-1.5 text-[#E63946]" />
          {label}
          <ChevronDown size={11} className="ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))]">
          Subscribe (auto-sync)
        </DropdownMenuLabel>
        <DropdownMenuItem onClick={(e) => { e.preventDefault(); doCopy('webcal'); }} data-testid="cal-copy-webcal" className="cursor-pointer">
          <Smartphone size={13} className="mr-2 text-[#E63946]" />
          <div className="flex-1">
            <p className="text-[11px] font-semibold">iPhone / Mac</p>
            <p className="text-[9px] text-[hsl(var(--text-muted))]">Copy webcal:// URL</p>
          </div>
          {copied === 'webcal' ? <CheckCircle size={12} className="text-[#10B981]" /> : <Copy size={11} className="text-[hsl(var(--text-muted))]" />}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={(e) => { e.preventDefault(); doCopy('https'); }} data-testid="cal-copy-https" className="cursor-pointer">
          <MonitorSmartphone size={13} className="mr-2 text-[#3B82F6]" />
          <div className="flex-1">
            <p className="text-[11px] font-semibold">Google Calendar / Android</p>
            <p className="text-[9px] text-[hsl(var(--text-muted))]">Copy HTTPS URL</p>
          </div>
          {copied === 'https' ? <CheckCircle size={12} className="text-[#10B981]" /> : <Copy size={11} className="text-[hsl(var(--text-muted))]" />}
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-[hsl(var(--border))]" />
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-muted))]">
          One-time
        </DropdownMenuLabel>
        <DropdownMenuItem asChild data-testid="cal-download-ics" className="cursor-pointer">
          <a href={icalUrl || '#'} download className="flex items-center">
            <Download size={13} className="mr-2 text-[#8B5CF6]" />
            <div className="flex-1">
              <p className="text-[11px] font-semibold">Download .ics</p>
              <p className="text-[9px] text-[hsl(var(--text-muted))]">Import satu kali</p>
            </div>
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-[hsl(var(--border))]" />
        <DropdownMenuItem onClick={(e) => { e.preventDefault(); setShowGuide((s) => !s); }} data-testid="cal-toggle-guide" className="cursor-pointer">
          <HelpCircle size={13} className="mr-2 text-[#F59E0B]" />
          <span className="text-[11px]">{showGuide ? 'Sembunyikan panduan' : 'Bagaimana cara guna?'}</span>
        </DropdownMenuItem>
        {showGuide && (
          <div className="p-3 text-[10px] text-[hsl(var(--text-muted))] space-y-2 bg-[hsl(var(--surface))] border-t border-[hsl(var(--border))]" data-testid="cal-guide">
            <div>
              <b className="text-white">iPhone:</b> Klik <b>iPhone / Mac</b> → paste URL di Safari → tap <b>Subscribe</b>.
              Atau: <b>Settings → Calendar → Accounts → Add Account → Other → Add Subscribed Calendar</b>.
            </div>
            <div>
              <b className="text-white">Android / Google Calendar:</b> Klik <b>Google Calendar / Android</b> → buka
              Google Calendar → <b>Other calendars → + → From URL</b> → paste.
              Google Calendar akan sync ke phone Android automatik.
            </div>
            <div>
              <b className="text-white">Mac:</b> Buka <b>Calendar.app → File → New Calendar Subscription</b> → paste URL.
            </div>
            <div className="pt-1 text-[9px] italic">
              Reminder trigger native oleh calendar app anda. Kalau task berubah dalam CRM, calendar akan auto-refresh (biasanya setiap 1–6 jam).
            </div>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
